import { faUser } from '@fortawesome/free-regular-svg-icons';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import MultiSelect from 'components/common/MultiSelect';
import TreeSelect from 'components/common/TreeSelect';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import TemplateTaskSection from 'components/tickets/TemplateTaskSection';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import TaskTemplateService from 'services/TaskTemplate.service';
import TaskTemplateExplanation from './TaskTemplateExplanation';

const TaskTemplateNew = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const [serviceCatalogs, setServiceCatalogs] = useState([]);
  const [customers, setCustomers] = useState({
    currentPage: 0,
    hasNextPage: false,
    totalPages: 0,
    totalElementsPerPage: 20,
    totalElements: 0,
    content: []
  });

  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [selectedClients, setSelectedClients] = useState([]);

  const [handleModals, setHandleModals] = useState({
    categoryRegistration: false,
    categoryEdit: false,
    subcategoryRegistration: false,
    subcategoryEdit: false,
    error: false,
    success: false,
    dialogConfirm: false
  });
  const [error, setError] = useState({});
  const [success, setSuccess] = useState('');
  const [validated, setValidated] = useState(false);
  const form = useRef(null);
  const [body, setBody] = useState({
    title: '',
    description: '',
    serviceCatalog: { id: 0 },
    category: { id: 0 },
    subcategory: { id: 0 }
  });

  // Ref para o TemplateTaskSection
  const taskSectionRef = useRef();

  useEffect(() => {
    const initializeComponent = async () => {
      await Promise.all([fetchServiceCatalogs(), fetchCustomers()]);
    };

    initializeComponent();
  }, []);

  const fetchServiceCatalogs = async () => {
    try {
      const response = await ServiceCatalogService.listInTreeView();
      setServiceCatalogs(response);
    } catch (error) {
      console.error('Error fetching service catalogs:', error);
      showErrorNotification('Erro ao carregar catálogos de serviço');
    }
  };

  const fetchCustomers = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        includeInactives: false
      }).toString();
      const response = await api.customers.list(queryParams);
      setCustomers(response);
    } catch (error) {
      console.error('Error fetching customers:', error);
      showErrorNotification('Erro ao carregar clientes');
    }
  };

  const handleSetBody = (field, value) => {
    setBody(prevBody => ({ ...prevBody, [field]: value }));
  };

  const handleSelectServiceCatalog = selectedValue => {
    console.log('Valor selecionado no TreeSelect:', selectedValue);

    if (
      selectedValue &&
      Array.isArray(selectedValue) &&
      selectedValue.length === 3
    ) {
      const [serviceCatalogId, categoryId, subcategoryId] = selectedValue;

      // Verifica se todos os IDs são válidos
      if (serviceCatalogId && categoryId && subcategoryId) {
        console.log('Atualizando estados com:', {
          serviceCatalogId,
          categoryId,
          subcategoryId
        });

        setBody(prev => ({
          ...prev,
          serviceCatalog: { id: serviceCatalogId },
          category: { id: categoryId },
          subcategory: { id: subcategoryId }
        }));

        setSelectedSubcategory({
          serviceCatalogId: serviceCatalogId,
          categoryId: categoryId,
          id: subcategoryId
        });

        return;
      }
    }

    clearSubcategorySelection();
  };

  const clearSubcategorySelection = () => {
    console.log('Limpando seleção da subcategoria');
    setBody(prev => ({
      ...prev,
      serviceCatalog: { id: 0 },
      category: { id: 0 },
      subcategory: { id: 0 }
    }));
    setSelectedSubcategory(null);
  };

  const toggleModal = (modalName, value) => {
    setHandleModals(prevState => ({ ...prevState, [modalName]: value }));
  };

  const toggleAllClients = () => {
    setAllClientsSelected(!allClientsSelected);
    if (!allClientsSelected) {
      setSelectedClients([]);
    }
  };

  // Função de validação
  const validateForm = () => {
    const errors = [];

    if (!body.title.trim()) {
      errors.push('O título do template é obrigatório');
    }

    const tasksToSave = taskSectionRef.current?.getTasks();
    if (!tasksToSave || tasksToSave.length === 0) {
      errors.push('Adicione pelo menos uma tarefa ao template');
    }

    const hasSubcategory = body.subcategory.id !== 0;
    const hasClients = allClientsSelected || selectedClients.length > 0;

    if (hasSubcategory && !hasClients) {
      errors.push(
        'Selecione pelo menos um cliente ou marque "Todos os clientes" quando um catálogo estiver selecionado'
      );
    } else if (!hasSubcategory && hasClients) {
      errors.push(
        'Selecione um catálogo de serviço quando clientes estiverem selecionados'
      );
    }

    return errors;
  };

  const handleSaveTemplate = async () => {
    setIsSaving(true);

    try {
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => showErrorNotification(error));
        return;
      }

      const tasksToSave = taskSectionRef.current?.getTasks() || [];

      const templateData = {
        title: body.title,
        description: body.description,
        applyToAllCustomers: allClientsSelected,
        serviceCatalog: { id: body.serviceCatalog.id },
        category: { id: body.category.id },
        subcategory: { id: body.subcategory.id },
        customersIds: allClientsSelected
          ? [] // Quando applyToAllCustomers=true, customersIds pode ser vazio
          : selectedClients,
        taskTemplateItems: tasksToSave.map(task => ({
          title: task.title,
          isGroupTitle: task.isGroupTitle || false,
          description: task.description || '',
          deadlineDays:
            task.deadlineDays === null ||
            task.deadlineDays === undefined ||
            task.deadlineDays === '' ||
            task.days_to_complete === null ||
            task.days_to_complete === undefined ||
            task.days_to_complete === ''
              ? null
              : task.deadlineDays !== undefined && task.deadlineDays !== ''
              ? parseInt(task.deadlineDays)
              : parseInt(task.days_to_complete),
          status: false,
          agent: task.agent ? { id: task.agent.id } : null
        }))
      };

      if (
        body.serviceCatalog.id !== 0 &&
        body.category.id !== 0 &&
        body.subcategory.id !== 0
      ) {
        templateData.serviceCatalog = { id: body.serviceCatalog.id };
        templateData.category = { id: body.category.id };
        templateData.subcategory = { id: body.subcategory.id };
      } else {
        templateData.serviceCatalog = null;
        templateData.category = null;
        templateData.subcategory = null;
      }

      const response = await TaskTemplateService.create(templateData);

      if (response && !response.status) {
        showSuccessNotification('Template criado com sucesso!');
        navigate('/settings/task-templates');
      } else {
        throw new Error(response?.userMessage || 'Erro ao salvar template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showErrorNotification(error.message || `Erro ao criar template ${error}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Gera uma key única para forçar re-render do TreeSelect
  const getTreeSelectKey = () => {
    if (selectedSubcategory) {
      return `treeselect-${selectedSubcategory.serviceCatalogId}-${selectedSubcategory.categoryId}-${selectedSubcategory.id}`;
    }
    return `treeselect-empty-${Date.now()}`;
  };

  return (
    <>
      <SuccessRequest
        message={success}
        showAlert={handleModals.success}
        setShowAlert={() => toggleModal('success', false)}
      />
      <FailedRequest
        message={error.description}
        showAlert={handleModals.error}
        setShowAlert={() => toggleModal('error', false)}
      />

      <Row className="g-3">
        <Col xl={9}>
          <FalconComponentCard>
            <Flex alignItems="center">
              <Card.Header>
                <Flex
                  justifyContent="between"
                  alignItems="center"
                  className="w-100"
                >
                  <IconButton
                    onClick={handleCancel}
                    variant="falcon-default"
                    size="sm"
                    icon="arrow-left"
                    className="me-2"
                  />
                  <h3>Novo Template</h3>
                </Flex>
              </Card.Header>
            </Flex>

            <FalconComponentCard.Body>
              <Form validated={validated} ref={form}>
                {/* Título do Template */}
                <Row className="mb-3">
                  <Col lg={8}>
                    <Form.Group>
                      <Form.Label className="mb-2">
                        Título do Template{' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: Template para Desenvolvimento de Software"
                        required
                        value={body.title}
                        onChange={e => handleSetBody('title', e.target.value)}
                        className="py-2"
                      />
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        Insira um título
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Descrição do Template */}
                <Row className="mb-3">
                  <Col lg={8}>
                    <Form.Group>
                      <Form.Label className="mb-2">
                        Descrição do Template
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        placeholder="Descreva quando e como este template deve ser usado..."
                        rows={6}
                        value={body.description}
                        onChange={e =>
                          handleSetBody('description', e.target.value)
                        }
                        className="py-2"
                      />
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        Insira uma descrição
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Catálogo de Serviço com TreeSelect */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="mb-2">
                        Catálogo de serviço
                      </Form.Label>
                      <div className="p-2">
                        <div className="d-flex align-items-center gap-2">
                          <div className="flex-grow-1">
                            <TreeSelect
                              key={getTreeSelectKey()}
                              data={serviceCatalogs}
                              onSelect={handleSelectServiceCatalog}
                              initialValue={
                                selectedSubcategory
                                  ? [
                                      selectedSubcategory.serviceCatalogId,
                                      selectedSubcategory.categoryId,
                                      selectedSubcategory.id
                                    ]
                                  : []
                              }
                              showPlaceholder={true}
                            />
                          </div>
                          {selectedSubcategory && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={clearSubcategorySelection}
                              title="Limpar seleção"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Associação de Clientes */}
                <Row className="mb-0">
                  <Col>
                    <div className="mb-3">
                      <Form.Label className="mb-2">
                        Defina quais clientes podem usar este template
                      </Form.Label>

                      <div className="border rounded p-3 mb-3">
                        <Flex
                          alignItems="center"
                          className="mb-2"
                          justifyContent={'between'}
                        >
                          <div>
                            <FontAwesomeIcon
                              icon={faUser}
                              className="me-2 text-primary"
                            />
                            <span className="me-2">Todos os clientes</span>
                          </div>
                          <Form.Check
                            type="switch"
                            name="clientAssociation"
                            id="allClients"
                            checked={allClientsSelected}
                            onChange={toggleAllClients}
                            className="fw-semibold"
                          />
                        </Flex>
                        {allClientsSelected && (
                          <p className="mb-0 ms-4">
                            Este template estará disponível para todos os
                            clientes
                          </p>
                        )}
                      </div>

                      {!allClientsSelected && (
                        <div className="border rounded p-3">
                          <p>Clientes Disponíveis</p>

                          <div className="p-2">
                            <MultiSelect
                              placeholder="Selecione os clientes..."
                              options={customers.content.map(client => ({
                                value: client.id,
                                label: client.name || client.fantasyName
                              }))}
                              value={selectedClients.map(clientId => {
                                const client = customers.content.find(
                                  c => c.id === clientId
                                );
                                return {
                                  value: clientId,
                                  label: client
                                    ? client.name || client.fantasyName
                                    : clientId
                                };
                              })}
                              onChange={selectedOptions => {
                                setSelectedClients(
                                  selectedOptions.map(option => option.value)
                                );
                              }}
                              isSearchable
                              isMulti
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Seção de Tarefas */}
                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label className="mb-2">
                        Tarefas do Template{' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="border rounded p-3">
                        <TemplateTaskSection
                          ref={taskSectionRef}
                          onCountChange={() => {}}
                          initialTasks={[]} // vazio para novo template
                          tasksLoaded={true} // true para novo template
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Botões de Ação */}
                <Row>
                  <Col>
                    <Flex
                      justifyContent="end"
                      gap={2}
                      className="pt-3"
                      alignItems={'center'}
                    >
                      <Button
                        onClick={handleCancel}
                        variant="falcon-default"
                        icon="arrow-left"
                        disabled={isSaving}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveTemplate}
                        className="ms-1"
                        variant="primary"
                        disabled={isSaving}
                      >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                        <FontAwesomeIcon icon={faSave} className="ms-1" />
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>

        <Col xl={3}>
          <TaskTemplateExplanation />
        </Col>
      </Row>
    </>
  );
};

export default TaskTemplateNew;
